import { ManifoldWindow } from 'build/mount';
import { inject, provide } from 'vue';
import { mixins, Options, Vue } from 'vue-class-component';
import { Store } from 'vuex';
import { EthereumNetwork } from '@manifoldxyz/manifold-sdk';
import { CAMPAIGN_STORE } from '@/constants/campaign';

/*
 * @dev:
 * Adding a new prop? Declare it in two places:
 *   1. MWidgetOptionsProps with type and default
 *   2. MWidgetProps (theres an obvious pattern)
 *
 * Optionally, enforce any new prop-dependency rules
 * in the created() hook of MWidgetProvidePropsMixin.
 */
export const MCheckoutWidgetOptionsProps = {
  network: {
    type: Number,
    default: EthereumNetwork.MAINNET
  },
  clientId: {
    type: String,
    default: ''
  },
  grantType: {
    type: String,
    default: 'signature'
  },
  appName: {
    type: String,
    default: ''
  },
  cartIconSection: {
    type: String,
    default: ''
  },
  campaignId: {
    type: Number,
    default: undefined
  },
  shopifyItemProductId: {
    type: Number,
    default: undefined
  },
  shopifyItemVariantId: {
    type: Number,
    default: undefined
  },
  shopifyItemTokenAddress: {
    type: String,
    default: ''
  },
  shopifyItemTokenId: {
    type: String,
    default: ''
  },
  // From Checkout.vue
  checkoutText: {
    type: String,
    default: 'Checkout with NFTs'
  },
  clearCartText: {
    type: String,
    default: 'Clear Cart'
  },
  connectWalletText: {
    type: String,
    default: 'Connect Wallet'
  },
  alreadyExistingCheckoutMessage: {
    type: String,
    default: 'You already have an existing checkout. Please complete it before starting a new one.'
  },
  // From MTokenCartItem
  productHandle: {
    type: String,
    default: ''
  },
  quantity: {
    type: Number,
    default: 0
  }
};

export type MCheckoutWidgetOptionsPropsType = keyof typeof MCheckoutWidgetOptionsProps;

/*
 * Defined once and extended so that the inject() and provide() calls made by
 * the two mixins in this file define all of the same things.
 */
export class MCheckoutWidgetProps extends Vue {
  // follow the pattern below when adding new props
  network = MCheckoutWidgetOptionsProps.network.default;
  clientId = MCheckoutWidgetOptionsProps.clientId.default;
  grantType = MCheckoutWidgetOptionsProps.grantType.default;
  appName = MCheckoutWidgetOptionsProps.appName.default;
  cartIconSection = MCheckoutWidgetOptionsProps.cartIconSection.default;
  campaignId = MCheckoutWidgetOptionsProps.campaignId.default;
  shopifyItemProductId = MCheckoutWidgetOptionsProps.shopifyItemProductId.default;
  shopifyItemVariantId = MCheckoutWidgetOptionsProps.shopifyItemVariantId.default;
  shopifyItemTokenAddress = MCheckoutWidgetOptionsProps.shopifyItemTokenAddress.default;
  shopifyItemTokenId = MCheckoutWidgetOptionsProps.shopifyItemTokenId.default;
  checkoutText = MCheckoutWidgetOptionsProps.checkoutText.default;
  clearCartText = MCheckoutWidgetOptionsProps.clearCartText.default;
  connectWalletText = MCheckoutWidgetOptionsProps.connectWalletText.default;
  alreadyExistingCheckoutMessage =
    MCheckoutWidgetOptionsProps.alreadyExistingCheckoutMessage.default;
  productHandle = MCheckoutWidgetOptionsProps.productHandle.default;
  quantity = MCheckoutWidgetOptionsProps.quantity.default;
}

/*
 * Used by WalletMixin to inject() back in all parent-provided props as variables
 * Reference: https://vuejs.org/guide/components/provide-inject.html
 */
export class MCheckoutWidgetInjectContext extends mixins(MCheckoutWidgetProps) {
  $store: Store<any>;
  mmid = '';
  loading = true;

  created(): void {
    // get all property definitions declared in MCampaignProvidePropsMixin
    const keys = Object.keys(new MCheckoutWidgetProps({}));
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      (this as any)[key] = inject(key); // eslint-disable-line
    }

    // get additional properties
    this.mmid = inject('mmid') || '';
    this.$store = inject('$store') || (window as Window as ManifoldWindow)[CAMPAIGN_STORE];
  }
}

// TODO: Come back to this and see if I need more -- actually might not need these at all
const CheckoutPropsSavedToStore: string[] = [
  // 'network'
];

/**
 * Props that might take a computed default value set in MCampaignPropProvider.created().
 * Mapping of Vue prop name to MCampaignPropProvider internal property name.
 *
 * @dev: add the corresponding property to MCampaignPropProvider and the computed logic
 * to MCampaignPropProvider.created().
 */
const OverriddenProps = {
  // Examples from marketplace: -- Dont think there are any here?
  // version: 'marketplaceVersion',
  // address: 'contractAddress',
  // referralAddress: 'referrer',
  // network: 'network'
} as const;

type OverriddenPropKey = keyof typeof OverriddenProps;

function isOverriddenPropKey(key: string): key is OverriddenPropKey {
  return key in OverriddenProps;
}

/*
 * Used by MWidget.vue to provide() all its props to children components
 * Reference: https://vuejs.org/guide/components/provide-inject.html
 */
@Options({
  props: MCheckoutWidgetOptionsProps
})
export class MCheckoutWidgetPropProvider extends mixins(MCheckoutWidgetProps) {
  // Props from this file
  cartIconSection: string;
  mmid = '';
  loading = true;

  // Important to note - the async calls in here are not resolved
  // before the component is mounted. So state is not always defined.
  async created() {
    // Check for required props
    if (![EthereumNetwork.MAINNET, EthereumNetwork.GOERLI].includes(this.network)) {
      throw new Error(`Unsupported network "${this.network.toString()}".`);
    }
    if ((this.appName || this.clientId) && !(this.appName && this.clientId)) {
      throw Error('Props app-name and client-id must be provided together');
    }

    // Take all the additional props potentially passed into the widget
    // and set those corresponding values in the store.
    this.saveProvidedPropsToStore();
    this.provideProps();
  }

  // Private getter for $store -- taken from Jaxon marketplace widget to continue using this.$store everywhere
  get $store(): Store<any> {
    const store = (window as Window as ManifoldWindow)[CAMPAIGN_STORE];
    if (!store) {
      // shouldn't happen...
      throw new Error('Marketplace: No store found.');
    }
    return store;
  }

  private saveProvidedPropsToStore(): void {
    CheckoutPropsSavedToStore.forEach((propName) => {
      this.$store.commit(
        `${this.mmid}/set${propName.charAt(0).toUpperCase()}${propName.substring(1)}`,
        this.getPropValue(propName),
        { root: true }
      );
    });
  }

  /**
   * This function sets the props in a look up table for the children components to get from if needed.
   * This follows the provide()/inject() pattern in Vue -- otherwise known as prop "drilling" so we
   * don't have to pass props down multiple levels.
   */
  private provideProps(): void {
    // define all properties for children who use the MMarketplaceInjectContext mixin
    const keys = Object.keys(new MCheckoutWidgetProps({}));
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      provide(key, this.getPropValue(key));
    }

    // Provide the mmid and $store to children components.
    // (Store them in a lookup table so children classes/components can access them.)
    // provide('mmid', this.mmid);
    provide('mmid', `this.campaignId`);
    provide('$store', this.$store);
  }

  private getPropValue(propName: string): any {
    let value = (this as any)[propName];
    if (isOverriddenPropKey(propName)) {
      value = this[OverriddenProps[propName]];
    }
    return value;
  }
}
